import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import PageLayout from "../components/layout/PageLayout";
import { fetchWithAuth, useUser } from "../Context/UserContext";
import { useCompany } from "../Context/CompanyContext";
import { LoadingOverlay } from "../components/ui/loading-overlay";
import API_URL from "../config";
import { Helmet } from "react-helmet-async";
import { X } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import { toast } from "sonner";

const Reseñas = () => {
  const { companies } = useCompany();
  const { user } = useUser();
  const location = useLocation();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [reviewTitle, setReviewTitle] = useState("");
  const [reviewComment, setReviewComment] = useState("");
  const [criteria, setCriteria] = useState([]);
  const [hasTitleBeenModified, setHasTitleBeenModified] = useState(false);
  const [fechaExperiencia, setFechaExperiencia] = useState("");
  const today = new Date();
  const oneYearAgo = new Date();
  oneYearAgo.setFullYear(today.getFullYear() - 1);
  const minDate = oneYearAgo.toISOString().split("T")[0]; // Formato YYYY-MM-DD
  const maxDate = today.toISOString().split("T")[0]; // Formato de hoy
  // Variables de edicion de review
  const { reviewId, score: initialScore, reviewTarget } = location.state || {};
  const [reviewScore, setReviewScore] = useState(initialScore || 0);
  const [isEditing] = useState(!!reviewId);
  const [reviewTargetState, setReviewTargetState] = useState(
    reviewTarget || {
      id_empresa: null,
      nombre_empresa: "",
      id_user: null,
      nombre: "",
      apellido: "",
    }
  );

  useEffect(() => {
    if (isEditing) {
      setIsLoading(true);
      const fetchReviewData = async () => {
        try {
          const response = await fetchWithAuth(`${API_URL}/review/${reviewId}`);
          const data = await response.json();

          console.log("🚀 Datos recibidos del backend:", data); // 🔹 Imprimir en la consola del navegador

          if (response.ok) {
            setReviewScore(data.puntaje_global);
            setReviewTitle(data.titulo);
            setReviewComment(data.comentario);
            setCriteria(data.criterios || []);
            setFechaExperiencia(data.fecha_experiencia || "");

            // Si `reviewTargetState` aún no está definido, llenarlo con los datos de la reseña
            setReviewTargetState((prevState) => ({
              id_empresa: prevState.id_empresa || data.id_company || null,
              nombre_empresa: prevState.nombre_empresa || data.nombre_empresa || "",
              id_user: prevState.id_user || data.id_user || null,
              nombre: prevState.nombre || data.nombre_usuario || "",
              apellido: prevState.apellido || data.apellido_usuario || "",
            }));
          } else {
            console.error("Error al obtener la reseña:", data.error);
          }
        } catch (error) {
          console.error("Error en la solicitud:", error);
        } finally {
          setIsLoading(false);
        }
      };

      fetchReviewData();
    }
  }, [isEditing, reviewId]);

  const isOwnCompany =
    companies?.company?.id_company === Number(reviewTarget?.id_empresa);

  const handleAddCriteria = () => {
    if (criteria.length < 10) {
      let newCriteria = [];
      if (criteria.length === 0) {
        // Si no hay criterios, agregar 3 al primer clic
        newCriteria = [
          { name: "Calidad del servicio", score: 5 },
          { name: "Servicio post-venta", score: 5 },
          { name: "Relación calidad-precio", score: 5 },
        ];
      } else {
        // Agregar solo 1 criterio para los clics siguientes
        newCriteria = [{ name: "", score: 5 }];
      }

      // Concatenar nuevos criterios y limitar a un máximo de 15
      const updatedCriteria = [...criteria, ...newCriteria].slice(0, 15);
      setCriteria(updatedCriteria);

      // Recalcular el puntaje global basado en los nuevos criterios
      const totalScore =
        updatedCriteria.reduce((acc, cur) => acc + cur.score, 0) /
        updatedCriteria.length;
      setReviewScore(totalScore);
    }
  };

  const handleRemoveCriteria = (index) => {
    const updatedCriteria = criteria.filter((_, i) => i !== index);
    setCriteria(updatedCriteria);

    // Recalcular el puntaje global basado en los criterios restantes
    const totalScore =
      updatedCriteria.length > 0
        ? updatedCriteria.reduce((acc, cur) => acc + cur.score, 0) /
          updatedCriteria.length
        : 0; // Si no hay criterios, el puntaje global será 0
    setReviewScore(totalScore);
  };

  const handleCriteriaChange = (index, field, value) => {
    const updatedCriteria = criteria.map((c, i) =>
      i === index ? { ...c, [field]: value } : c
    );
    setCriteria(updatedCriteria);

    const totalScore =
      updatedCriteria.reduce((acc, cur) => acc + cur.score, 0) /
      updatedCriteria.length;
    setReviewScore(totalScore);
  };

  const handleSubmitReview = async () => {
    setIsLoading(true);
    const calculatedScore =
      criteria.length > 0
        ? criteria.reduce((acc, cur) => acc + cur.score, 0) / criteria.length
        : reviewScore;

    // Obtener la fecha actual y la fecha hace un año
    const today = new Date();
    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(today.getFullYear() - 1);

    // Validar campos obligatorios
    if (!reviewComment.trim()) {
      toast.error("El comentario es obligatorio.");
      return;
    }

    if (!fechaExperiencia) {
      toast.error("Debes ingresar la fecha de experiencia.");
      return;
    }

    if (!reviewTitle.trim()) {
      toast.error("El título es obligatorio.");
      return;
    }

    // Validar que la fecha de experiencia no sea mayor a 1 año
    const selectedDate = new Date(fechaExperiencia);
    if (selectedDate > today || selectedDate < oneYearAgo) {
      toast.error("La fecha de experiencia debe ser dentro del último año.");
      return;
    }

    if (!calculatedScore || calculatedScore < 1) {
      toast.error("Debes asignar una calificación.");
      return;
    }

    const reviewData = {
      titulo: reviewTitle.trim(),
      id_company: reviewTarget?.id_empresa ? reviewTarget.id_empresa : null,
      id_user: reviewTarget?.id_user ? reviewTarget.id_user : null,
      creador_review: null,
      comentario: reviewComment.trim(),
      tipo_review: reviewTarget?.id_empresa ? "empresa" : "usuario",
      fecha_experiencia: fechaExperiencia,
      criterios: criteria.length > 0 ? criteria : null,
      puntaje_global: calculatedScore,
    };

    try {
      let response;
      if (isEditing) {
        response = await fetchWithAuth(`${API_URL}/review/${reviewId}/edit`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(reviewData),
        });
      } else {
        response = await fetchWithAuth(`${API_URL}/review/create`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(reviewData),
        });
      }

      if (response.ok) {
        toast.success(
          isEditing
            ? "Reseña actualizada exitosamente."
            : "Reseña enviada exitosamente."
        );

        if (isEditing) {
          if (reviewTargetState?.id_empresa) {
            navigate(
              `/company-profile/${
                reviewTargetState.id_empresa
              }/${encodeURIComponent(reviewTargetState.nombre_empresa)}`
            );
          } else if (reviewTargetState?.id_user) {
            navigate(
              `/profile/${reviewTargetState.id_user}/${encodeURIComponent(
                `${reviewTargetState.nombre} ${reviewTargetState.apellido}`
              )}`
            );
          } else {
            navigate(
              `/profile/${user?.user?.id_user}/${encodeURIComponent(
                `${user?.user?.nombre} ${user?.user?.apellido}`
              )}`
            );
          }
        } else {
          if (reviewTargetState?.id_empresa) {
            navigate(
              `/company-profile/${
                reviewTargetState.id_empresa
              }/${encodeURIComponent(reviewTargetState.nombre_empresa)}`
            );
          } else if (reviewTargetState?.id_user) {
            navigate(
              `/profile/${reviewTargetState.id_user}/${encodeURIComponent(
                `${reviewTargetState.nombre} ${reviewTargetState.apellido}`
              )}`
            );
          }
        }
      } else {
        const errorData = await response.json();
        toast.error(`Error al enviar la reseña: ${errorData.error}`);
      }
    } catch (error) {
      console.error("Error al enviar la reseña:", error);
      toast.error("Ocurrió un error al intentar enviar la reseña.");
    } finally {
      setIsLoading(false);
    }
  };

  const starColors = ["#d62246", "#f97316", "#eab308", "#4bbf73", "#2A9D8F"];
  const placeholders = [
    "¿Qué salió mal esta vez? ¿Cómo podría mejorar esta empresa? Recuerda ser honesto, útil y constructivo.",
    "¿Qué aspectos te gustaron y cuáles no? ¿Qué hace bien esta empresa o cómo puede mejorar? Recuerda ser honesto, útil y constructivo.",
    "¿Qué hizo que tu experiencia fuera genial? ¿Qué destaca de esta empresa o servicio? Recuerda ser honesto, útil y constructivo.",
  ];

  if (isOwnCompany) {
    return (
      <PageLayout>
        <div className="mx-auto max-w-xl px-4 py-16 text-center">
          <h2 className="text-xl font-semibold text-foreground">
            No puedes dejar una reseña a tu propia empresa.
          </h2>
          <Button className="mt-4" onClick={() => (window.location.href = "/catalogo")}>
            Ir al Catálogo
          </Button>
        </div>
      </PageLayout>
    );
  }

  // Si está cargando, mostrar el componente Cargando
  if (isLoading) {
    return <LoadingOverlay />;
  }

  const inputClass =
    "w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary";

  return (
    <PageLayout>
      <Helmet>
        <title>{isEditing ? "Editar Reseña - Procu" : "Dejar una Reseña - Procu"}</title>
        <meta
          name="description"
          content="Comparte tu experiencia y deja una reseña en Procu. Evalúa empresas y proveedores para ayudar a otros usuarios a tomar mejores decisiones."
        />
        <meta name="keywords" content="reseñas, evaluación, empresas, proveedores, comentarios, opiniones, Procu" />
      </Helmet>

      <div className="mx-auto max-w-2xl space-y-6 px-4 py-8 sm:px-6">
        <h2 className="text-xl font-semibold text-foreground">
          {reviewTargetState?.nombre_empresa ? (
            <span>
              Dejar Reseña para{" "}
              <a
                href={`/company-profile/${
                  reviewTargetState.id_empresa
                }/${encodeURIComponent(reviewTargetState.nombre_empresa)}`}
                className="text-primary hover:underline"
              >
                {reviewTargetState.nombre_empresa}
              </a>
            </span>
          ) : (
            <span>
              Dejar Reseña para{" "}
              <a
                href={`/profile/${reviewTargetState?.id_user || ""}/${encodeURIComponent(
                  `${reviewTargetState?.nombre || "Usuario desconocido"} ${
                    reviewTargetState?.apellido || ""
                  }`
                )}`}
                className="text-primary hover:underline"
              >
                {reviewTargetState?.nombre || "Usuario desconocido"} {reviewTargetState?.apellido || ""}
              </a>
            </span>
          )}
        </h2>

        <Card>
          <CardContent className="space-y-2 pt-6">
            <small className="text-xs text-muted-foreground">Puntaje:</small>
            <div className="flex items-center gap-2">
              <div className="flex">
                {Array(5)
                  .fill(0)
                  .map((_, index) => {
                    const roundedScore = Math.round(reviewScore * 2) / 2; // Redondea al medio más cercano (0.5 pasos)
                    const isFull = index + 1 <= Math.floor(roundedScore); // Estrella completamente llena
                    const isHalf = index + 1 === Math.ceil(roundedScore) && !isFull; // Estrella parcialmente llena

                    // Aplicar el mismo color a todas las estrellas según el puntaje global redondeado
                    const colorIndex = Math.min(Math.ceil(reviewScore), starColors.length) - 1;

                    return (
                      <button
                        key={index}
                        type="button"
                        className="flex h-8 w-8 items-center justify-center rounded-full text-lg"
                        style={{
                          background: isFull
                            ? starColors[colorIndex] // Todas las estrellas llenas del mismo color
                            : isHalf
                            ? `linear-gradient(90deg, ${starColors[colorIndex]} 50%, #ccc 50%)` // Color parcial para medias estrellas
                            : "#ccc",
                          color: "white",
                        }}
                        onClick={() =>
                          criteria.length > 0
                            ? toast.error(
                                "No puedes modificar el puntaje global cuando hay criterios adicionales."
                              )
                            : setReviewScore(index + 1)
                        }
                      >
                        ★
                      </button>
                    );
                  })}
              </div>
              <p className="text-sm text-muted-foreground">: {reviewScore.toFixed(1)}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="space-y-3 pt-6">
            <small className="text-xs text-muted-foreground">
              Si deseas, puedes escoger criterios para mejorar tu reseña (Max 10):
            </small>

            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleAddCriteria}
              disabled={criteria.length >= 10}
            >
              Agregar criterios
            </Button>

            {criteria.map((criterion, index) => (
              <div key={index} className="flex items-center gap-2 border-t border-border pt-3">
                <button
                  type="button"
                  onClick={() => handleRemoveCriteria(index)}
                  className="text-muted-foreground hover:text-destructive"
                >
                  <X className="h-4 w-4" />
                </button>
                <Input
                  type="text"
                  placeholder="Nombre del criterio"
                  value={criterion.name}
                  onChange={(e) => handleCriteriaChange(index, "name", e.target.value)}
                  className="flex-1"
                />
                <div className="flex">
                  {Array(5)
                    .fill(0)
                    .map((_, starIndex) => (
                      <button
                        key={starIndex}
                        type="button"
                        className="flex h-7 w-7 items-center justify-center rounded-full text-sm"
                        style={{
                          backgroundColor:
                            criterion.score >= starIndex + 1 ? starColors[criterion.score - 1] : "#ccc",
                          color: "white",
                        }}
                        onClick={() => handleCriteriaChange(index, "score", starIndex + 1)}
                      >
                        ★
                      </button>
                    ))}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="space-y-4 pt-6">
            <div>
              <h3 className="text-sm font-semibold text-foreground">Cuéntanos de tu experiencia:</h3>
              <textarea
                className={`${inputClass} mt-1.5`}
                rows={5}
                placeholder={
                  placeholders[reviewScore <= 2 ? 0 : reviewScore === 3 ? 1 : 2] ||
                  "Describe tu experiencia."
                }
                value={reviewComment}
                onChange={(e) => {
                  const newComment = e.target.value;
                  setReviewComment(newComment);

                  // Solo sincronizar el título si no ha sido modificado manualmente
                  if (!hasTitleBeenModified) {
                    const firstWords = newComment.split(" ").slice(0, 10).join(" ");
                    setReviewTitle(firstWords);
                  }
                }}
                maxLength={4000}
              ></textarea>
              <small className="text-xs text-muted-foreground">{reviewComment.length}/4000 caracteres</small>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-foreground">Deja un título a tu review:</h3>
              <Input
                type="text"
                className="mt-1.5"
                placeholder="Título de tu reseña"
                value={reviewTitle}
                onChange={(e) => {
                  const newTitle = e.target.value.slice(0, 100); // Limitar a 100 caracteres
                  setReviewTitle(newTitle);
                  setHasTitleBeenModified(true);
                }}
              />
            </div>

            <div>
              <h3 className="text-sm font-semibold text-foreground">Fecha de experiencia:</h3>
              <small className="text-xs text-muted-foreground">
                Considera experiencias que hayas tenido menores a un año.
              </small>
              <Input
                type="date"
                className="mt-1.5"
                value={fechaExperiencia}
                min={minDate} // No permite seleccionar antes de hace 1 año
                max={maxDate} // No permite seleccionar fechas futuras
                onChange={(e) => setFechaExperiencia(e.target.value)}
              />
            </div>
          </CardContent>
        </Card>

        <div className="space-y-3 text-center">
          <h3 className="text-sm font-semibold text-foreground">Confirma el envío de tu reseña:</h3>
          <Button onClick={handleSubmitReview}>
            {isEditing ? "Actualizar Reseña" : "Enviar Reseña"}
          </Button>

          <p className="text-xs text-muted-foreground">
            Al continuar, aceptas las{" "}
            <Link className="text-primary hover:underline" to="/condiciones-uso">
              Condiciones de uso
            </Link>{" "}
            y el{" "}
            <Link className="text-primary hover:underline" to="/aviso-privacidad">
              Aviso de privacidad
            </Link>{" "}
            de Procu.
          </p>
        </div>
      </div>
    </PageLayout>
  );
};

export default Reseñas;
